import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  Header,
} from '@tanstack/react-table';
import { Icon } from '@cotera/client/app/components';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Extract table header cell component to reduce complexity
interface TableHeaderCellProps<T> {
  header: Header<T, unknown>;
  enableSorting: boolean;
  enableFiltering: boolean;
}

function TableHeaderCell<T>({
  header,
  enableSorting,
  enableFiltering,
}: TableHeaderCellProps<T>) {
  if (header.isPlaceholder) {
    return null;
  }

  return (
    <div className="flex items-center space-x-2">
      <div
        className={
          header.column.getCanSort()
            ? 'cursor-pointer select-none flex items-center space-x-1'
            : ''
        }
        onClick={header.column.getToggleSortingHandler()}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {enableSorting && header.column.getCanSort() && (
          <span className="ml-1">
            {{
              asc: <Icon icon="chevron-up" className="w-4 h-4" />,
              desc: <Icon icon="chevron-down" className="w-4 h-4" />,
            }[header.column.getIsSorted() as string] ?? (
              <Icon icon="chevron-up-down" className="w-4 h-4 text-gray-400" />
            )}
          </span>
        )}
      </div>
      {enableFiltering && header.column.getCanFilter() && (
        <Input
          type="text"
          value={(header.column.getFilterValue() as string) ?? ''}
          onChange={(e) => header.column.setFilterValue(e.target.value)}
          placeholder="Filter..."
          className="mt-1 h-7 text-xs"
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

// Extract pagination controls component
interface PaginationControlsProps {
  table: any;
}

function PaginationControls({ table }: PaginationControlsProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {'<'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {'>'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </Button>
      </div>
      <span className="text-sm text-gray-700">
        Page {table.getState().pagination.pageIndex + 1} of{' '}
        {table.getPageCount()}
      </span>
      <Select
        value={table.getState().pagination.pageSize.toString()}
        onValueChange={(value) => {
          table.setPageSize(Number(value));
        }}
      >
        <SelectTrigger className="w-[130px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[10, 20, 30, 40, 50].map((pageSizeOption) => (
            <SelectItem key={pageSizeOption} value={pageSizeOption.toString()}>
              Show {pageSizeOption}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Extract table body component
interface TableBodyProps<T> {
  table: any;
  columns: ColumnDef<T, any>[];
  onRowClick?: (row: T) => void;
}

function TableBody<T>({ table, columns, onRowClick }: TableBodyProps<T>) {
  if (table.getRowModel().rows.length === 0) {
    return (
      <tbody className="bg-white divide-y divide-gray-200">
        <tr>
          <td
            colSpan={columns.length}
            className="px-6 py-4 text-sm text-gray-500 text-center"
          >
            No data available
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {table.getRowModel().rows.map((row: any) => (
        <tr
          key={row.id}
          className={`hover:bg-gray-50${
            onRowClick !== undefined ? ' cursor-pointer' : ''
          }`}
          onClick={
            onRowClick !== undefined
              ? () => onRowClick(row.original)
              : undefined
          }
        >
          {row.getVisibleCells().map((cell: any) => (
            <td
              key={cell.id}
              className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

interface BasicTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
}

export function BasicTable<T>({
  data,
  columns,
  enableSorting = true,
  enableFiltering = true,
  enablePagination = false,
  pageSize = 10,
  maxHeight = '600px',
  onRowClick,
}: BasicTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  return (
    <div className="flex flex-col space-y-4">
      <div
        className="bg-white rounded-lg shadow overflow-auto"
        style={{ maxHeight }}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <TableHeaderCell
                      header={header}
                      enableSorting={enableSorting}
                      enableFiltering={enableFiltering}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <TableBody table={table} columns={columns} onRowClick={onRowClick} />
        </table>
      </div>

      {enablePagination && <PaginationControls table={table} />}
    </div>
  );
}
